const AbstractPaymentMethodConfig = require('@/lib/PaymentMethods/AbstractPaymentMethodConfig');
const { toBool, toInt, toString } = require('@/functions/transform');

class CloverConfig extends AbstractPaymentMethodConfig {
    constructor() {
        super();

        /**
         * Header: X-Clover-Device-Id
         * This is the device serial number and can be found in the Clover Dashboard.
         *
         * @type {string|null}
         */
        this.xCloverDeviceId = null;

        /**
         * Header: X-POS-Id
         * An identifier for the POS making the request. We will use the DigiTickets device.ID.
         *
         * @type {string|null}
         */
        this.xPosId = null;

        /**
         * The amount of time, in seconds, that this operation will process before timing out. A value of 0 indicates
         * no timeout will be enforced by Remote Pay, although protocol specified timeouts may still apply.
         *
         * @type {number}
         */
        this.xCloverTimeout = 0;

        /**
         * This can be found in the URL an action in a merchant dashboard.
         * e.g. /setupapp?mid=YTEDFDHZXYJB1
         *
         * @type {string|null}
         */
        this.merchantID = null;

        /**
         * The baseUrl of the API endpoint we call.
         * See https://docs.clover.com/reference/api-reference-overview
         * Development: https://sandbox.dev.clover.com
         * Production: https://api.eu.clover.com (UK and Europe)
         *
         * @type {string|null}
         */
        this.baseUrl = null;

        /**
         * Time in ms. Used when we need to poll after some actions for example when attempting to VOID a payment.
         *
         * @type {number}
         */
        this.pollPeriod = 1000;

        /**
         * Clover create a different merchant account for each physical location. So a company with 3 branches is
         * likely to have 3 merchant accounts.
         *
         * @type {boolean|null}
         */
        this.configAvailableForCurrentBranch = null;
    }

    /**
     * Returns true if this is a valid configuration. Otherwise, throws an error.
     *
     * @param {boolean} requireTerminal
     *
     * @return {boolean}
     */
    validate(requireTerminal = true) {
        const requiredInBackOffice = [
            // The presence of an authBearerToken indicates that the company has been successfully connected to Clover.
            // But we won't use this token as it's short-lived with a maximum lifetime of 30 minutes.
            // Instead, when we are about to make a request we check if the current token is valid and fetch a new
            // token if the existing token is invalid or within 3 minutes of expiry.
            'authBearerToken',
            'baseUrl',
            'merchantID',
        ];

        if (this.configAvailableForCurrentBranch === false) {
            throw new Error('No valid Clover config found for this branch. Please check the Clover payment method config in the Back Office and then perform a sync.');
        }

        for (let i = 0; i < requiredInBackOffice.length; i++) {
            if (!this[requiredInBackOffice[i]]) {
                throw new Error('The ' + requiredInBackOffice[i] + ' property must be set in the Clover payment method config in the Back Office.');
            }
        }

        if (!this.baseUrl.startsWith('https://')) {
            throw new Error("The baseUrl field should be a URL beginning with 'https://'. This field should be configured in the Clover payment method config in the Back Office.");
        }

        // After we have validated the other settings we can select a terminal. Therefore, we need to be able to
        // validate with or with the terminal selected.
        if (requireTerminal && !this.xCloverDeviceId) {
            throw new Error('Please select a terminal in the Clover Settings modal.');
        }

        return true;
    }

    getHydrationMap() {
        return {
            authBearerToken: toString,
            baseUrl: toString,
            merchantID: toString,
            configAvailableForCurrentBranch: toBool,
            pollPeriod: toInt,
            xCloverDeviceId: toString,
            xPosId: toString,
            xCloverTimeout: toInt,
        };
    }

    afterHydration() {
        // Temporarily rewrite the Clover production API URL this will be permanently updated in BO config.
        if (this.baseUrl === 'https://eu.clover.com') {
            this.baseUrl = 'https://api.eu.clover.com';
        }
    }
}

module.exports = CloverConfig;
